import { ROLE_AGENT, TENTATIVE_STATUSES } from '../domain/constants';
import type { Case, Staff } from '../domain/types';
import { sum } from '../lib/safeMath';
import { allocationsAt } from './capacity';

export const isActiveIn = (caseItem: Case, month: number): boolean =>
  month >= caseItem.startMonth && month <= caseItem.endMonth;

export const isTentative = (caseItem: Case): boolean =>
  TENTATIVE_STATUSES.some((status) => status === caseItem.status);

/** その月に案件へ配置されている量(人工)。 */
export const fteOf = (staff: readonly Staff[], caseItem: Case, month: number): number =>
  sum(
    staff.map((s) =>
      sum(
        allocationsAt(s, month)
          .filter((a) => a.caseId === caseItem.id)
          .map((a) => a.percent),
      ),
    ),
  ) / 100;

/** その月に案件へ 1% でも時間を使っている人数。 */
export const headsOf = (staff: readonly Staff[], caseItem: Case, month: number): number =>
  staff.filter((s) => allocationsAt(s, month).some((a) => a.caseId === caseItem.id)).length;

/** その月に必要な量(人工)の合計。 */
export const needAt = (cases: readonly Case[], month: number): number =>
  sum(cases.filter((c) => isActiveIn(c, month)).map((c) => c.requiredNinku));

/** 期間中の最大不足(人工)。0.05 未満は 0 とみなす。 */
export const shortOf = (staff: readonly Staff[], caseItem: Case): number => {
  let worst = 0;
  for (let month = caseItem.startMonth; month <= caseItem.endMonth; month++) {
    worst = Math.max(worst, caseItem.requiredNinku - fteOf(staff, caseItem, month));
  }
  return worst < 0.05 ? 0 : worst;
};

export type SenninViolation = { staff: Staff; caseItem: Case };

/** 専任案件の現場代理人が、他案件を兼務している組。 */
export const senninViolations = (
  cases: readonly Case[],
  staff: readonly Staff[],
  month = 0,
): SenninViolation[] =>
  cases
    .filter((c) => c.dedicatedAgent && isActiveIn(c, month))
    .flatMap((caseItem) =>
      staff.flatMap((s) => {
        const allocations = allocationsAt(s, month);
        const isAgent = allocations.some((a) => a.caseId === caseItem.id && a.role === ROLE_AGENT);
        return isAgent && allocations.length > 1 ? [{ staff: s, caseItem }] : [];
      }),
    );
